import type { ComboItem } from '@kardume/types';

/** Core modules an audit event can originate from. */
export type AuditModuleValue = 'BACKOFFICE' | 'MERCHANT' | 'CUSTOMER';

export const AUDIT_MODULE_LABELS: Record<string, string> = {
  BACKOFFICE: 'Backoffice',
  MERCHANT: 'Loja',
  CUSTOMER: 'App / Cliente',
  PLATFORM: 'Plataforma',
};

export const AUDIT_MODULE_OPTIONS: Array<{ value: AuditModuleValue; label: string }> = [
  { value: 'BACKOFFICE', label: 'Backoffice' },
  { value: 'MERCHANT', label: 'Loja' },
  { value: 'CUSTOMER', label: 'App / Cliente' },
];

export function moduleLabel(module?: string | null): string {
  if (!module) return '-';
  return AUDIT_MODULE_LABELS[module] ?? module;
}

/**
 * Friendly labels (pt-BR) for audit event types recorded by the core
 * (`platform_audit_event.event_type`). Unknown actions fall back to a
 * humanized version of the camelCase name.
 */
const AUDIT_ACTION_LABELS: Record<string, string> = {
  // Lojista (backoffice)
  MerchantCreated: 'Lojista criado',
  MerchantUpdated: 'Lojista atualizado',
  MerchantStatusChanged: 'Status do lojista alterado',
  MerchantApproved: 'Lojista aprovado',
  MerchantRejected: 'Lojista rejeitado',
  PromotionModerated: 'Anúncio moderado',
  PlanCreated: 'Plano criado',
  PlanUpdated: 'Plano atualizado',
  PlanStatusChanged: 'Status do plano alterado',
  EmployeeCreated: 'Funcionário criado',
  EmployeeUpdated: 'Funcionário atualizado',
  CategoryCreated: 'Categoria criada',
  CategoryUpdated: 'Categoria atualizada',
  CategoryDeleted: 'Categoria excluída',
  CategoriesReordered: 'Categorias reordenadas',
  CustomerStatusChanged: 'Status do cliente alterado',
  AccessProfileCreated: 'Perfil de acesso criado',
  AccessProfilePermissionsUpdated: 'Permissões do perfil atualizadas',
  BackofficePasswordChanged: 'Senha alterada',
  SubscriptionCreated: 'Assinatura criada',
  // Loja (merchant)
  MerchantOnboardingSubmitted: 'Cadastro de lojista enviado',
  CouponRedeemed: 'Cupom resgatado',
  // App / Cliente (customer)
  CustomerRegistered: 'Cliente cadastrado',
  CustomerLoggedIn: 'Cliente autenticado',
  ReferralRegistered: 'Indicação registrada',
};

/** Which module each action belongs to (matches the core package it lives in). */
const ACTION_MODULE: Record<string, AuditModuleValue> = {
  MerchantCreated: 'BACKOFFICE',
  MerchantUpdated: 'BACKOFFICE',
  MerchantStatusChanged: 'BACKOFFICE',
  MerchantApproved: 'BACKOFFICE',
  MerchantRejected: 'BACKOFFICE',
  PromotionModerated: 'BACKOFFICE',
  PlanCreated: 'BACKOFFICE',
  PlanUpdated: 'BACKOFFICE',
  PlanStatusChanged: 'BACKOFFICE',
  EmployeeCreated: 'BACKOFFICE',
  EmployeeUpdated: 'BACKOFFICE',
  CategoryCreated: 'BACKOFFICE',
  CategoryUpdated: 'BACKOFFICE',
  CategoryDeleted: 'BACKOFFICE',
  CategoriesReordered: 'BACKOFFICE',
  CustomerStatusChanged: 'BACKOFFICE',
  AccessProfileCreated: 'BACKOFFICE',
  AccessProfilePermissionsUpdated: 'BACKOFFICE',
  BackofficePasswordChanged: 'BACKOFFICE',
  SubscriptionCreated: 'BACKOFFICE',
  MerchantOnboardingSubmitted: 'MERCHANT',
  CouponRedeemed: 'MERCHANT',
  CustomerRegistered: 'CUSTOMER',
  CustomerLoggedIn: 'CUSTOMER',
  ReferralRegistered: 'CUSTOMER',
};

/** camelCase/PascalCase -> "Camel case" (fallback for unmapped actions). */
function humanize(action: string): string {
  const spaced = action
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .trim();
  if (!spaced) return action;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function formatAuditAction(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? humanize(action);
}

/** Options for the "Ação" combobox, limited to the actions of the given module. */
export function actionsForModule(module: string): ComboItem[] {
  return Object.keys(AUDIT_ACTION_LABELS)
    .filter((action) => ACTION_MODULE[action] === module)
    .map((action) => ({ id: action, label: formatAuditAction(action) }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}
